// src/pages/ColaboradorPage.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useLoadScript } from "@react-google-maps/api";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  Timestamp
} from "firebase/firestore";
import { db, callXeroFunction } from "../firebase.js";
import { useAuth } from "../context/AuthContext.js";
import { useLanguage } from "../context/LanguageContext.js";
import { t } from "../utils/translations.js";

import { getTravelMinutes } from "../utils/maps.js";
import { roundTravelMinute, floorTo5 } from "../utils/travelCalculations.js";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  InputGroup,
  FormControl,
  Modal,
  Spinner,
  Form
} from "react-bootstrap";
import { Combobox } from "@headlessui/react";
import {
  parseISO,
  startOfWeek,
  endOfWeek,
  subWeeks,
  addWeeks,
  format as fnsFormat,
  getISOWeek,
  eachDayOfInterval
} from "date-fns";

// 👇 constante fora do componente (evita recriar a cada render)
const LIBRARIES = ["places"];

export default function ColaboradorPage() {
  // 1) rota e auth
  const { slug, uid: paramUid } = useParams();
  const nome = decodeURIComponent(slug);
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const basePath =
    user?.isAdmin && paramUid
      ? `/admin/users/${paramUid}/colaborador/${slug}`
      : `/colaborador/${slug}`;
  const targetUid = user?.isAdmin && paramUid ? paramUid : user.uid;

  // 2) datas da semana atual (usa somente UMA vez useLocation()/URLSearchParams)
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const weekStartParam = params.get("weekStart"); // ex: "2025-05-19"

  const hoje = weekStartParam ? parseISO(weekStartParam) : new Date();

  // 3) datas da semana atual a partir de "hoje"
  const weekStart = startOfWeek(hoje, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(hoje, { weekStartsOn: 1 });
  const labelWeek = `${fnsFormat(weekStart, "dd MMM")} – ${fnsFormat(
    weekEnd,
    "dd MMM yyyy"
  )}`;

  // 3) reportId sempre com slug
  const isoWeek = getISOWeek(hoje);
  const reportId = `${targetUid}_${slug}_${fnsFormat(hoje, "yyyy")}-W${isoWeek}`;

  // 4) dias da semana
  const days = eachDayOfInterval({ start: weekStart, end: weekEnd }).map(d =>
    fnsFormat(d, "EEEE")
  );

  // 5) Google Maps — opções MEMOIZADAS para não recarregar o script
  const loaderOpts = useMemo(
    () => ({
      id: "google-map-script",
      googleMapsApiKey: process.env.REACT_APP_MAPS_API_KEY,
      libraries: LIBRARIES
    }),
    []
  );

  const { isLoaded, loadError } = useLoadScript(loaderOpts);

  useEffect(() => {
    if (isLoaded && window.google) {
      setAddressService(new window.google.maps.places.AutocompleteService());
    }
  }, [isLoaded]);

  // bounds aproximados de Auckland (só cria quando isLoaded for true)
  const aklBounds = isLoaded
    ? new window.google.maps.LatLngBounds(
        { lat: -37.0, lng: 173.5 },
        { lat: -36.5, lng: 175.0 }
      )
    : null;

  // estados principais
  const [clients, setClients] = useState([]);
  const [weeklyReport, setWeeklyReport] = useState(
    days.reduce((o, d) => ({ ...o, [d]: [] }), {})
  );
  const [numWorkers, setNumWorkers] = useState(
    days.reduce((o, d) => ({ ...o, [d]: 1 }), {})
  );
  const [searchQuery, setSearchQuery] = useState({});
  const [newClient, setNewClient] = useState({
    day: null,
    name: "",
    address: "",
    hours: "",
    visible: false
  });
  const [editModal, setEditModal] = useState(null);
  const [totals, setTotals] = useState(
    days.reduce((o, d) => ({ ...o, [d]: "00:00" }), {})
  );
  const [loadingTotals, setLoadingTotals] = useState(
    days.reduce((o, d) => ({ ...o, [d]: false }), {})
  );
  const [workMinDay, setWorkMinDay] = useState({});
  const [lunchMinDay, setLunchMinDay] = useState({});
  const [travelMinDay, setTravelMinDay] = useState({});
  const [loadingInit, setLoadingInit] = useState(true);
  const [addressService, setAddressService] = useState(null);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const addressDebounceRef = useRef(null);
  const addressSessionTokenRef = useRef(null);
  const [importingCalendar, setImportingCalendar] = useState(false);
  const [importToast, setImportToast] = useState(null);
  const [compareModal, setCompareModal] = useState({ show: false, calendarData: null, loading: false });

  // Ref para rastrear weeklyReport anterior e detectar mudanças por dia
  const prevWeeklyReportRef = useRef(weeklyReport);

  // utilitários de tempo
  const timeToMinutes = str => {
    if (!str) return 0;
    if (!str.includes(":")) return (parseInt(str) || 0) * 60;
    const [h, m] = str.split(":").map(Number);
    return (h || 0) * 60 + (m || 0);
  };
  const minutesToTime = mins => {
    const hh = Math.floor(mins / 60),
      mm = mins % 60;
    return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "00")}`;
  };

  // roundTravelMinute e floorTo5 agora importados de travelCalculations.js

  // Formata endereço para mostrar apenas: número, rua e bairro
  // Formato esperado: "85 Balmain Road, Birkenhead, Auckland, New Zealand"
  // Resultado esperado: "85 Balmain Road, Birkenhead"
  const formatAddress = (fullAddress) => {
    if (!fullAddress) return "";
    // Separa por vírgula e pega os 2 primeiros componentes (rua e bairro)
    const parts = fullAddress.split(",").map(p => p.trim());
    if (parts.length >= 2) {
      return `${parts[0]}, ${parts[1]}`;
    }
    return fullAddress.trim();
  };

  // calcula tempo de viagem via Google Maps (JS API)
  const getTravelTime = ({ origins, destinations }) =>
    new Promise(resolve => {
      if (!window.google?.maps?.DistanceMatrixService) return resolve(0);
      const svc = new window.google.maps.DistanceMatrixService();
      svc.getDistanceMatrix(
        {
          origins,
          destinations,
          travelMode: window.google.maps.TravelMode.DRIVING,
          unitSystem: window.google.maps.UnitSystem.METRIC
        },
        (resp, status) => {
          if (status === "OK") {
            const e = resp.rows[0].elements[0];
            resolve(e.status === "OK" ? Math.floor(e.duration.value / 60) : 0);
          } else resolve(0);
        }
      );
    });

  // 6) carrega lista de clients do PostgreSQL
  useEffect(() => {
    const loadClients = async () => {
      try {
        const result = await callXeroFunction('getClients', {}, { method: 'GET' });
        if (result.clients) {
          // Normaliza campos para compatibilidade (PostgreSQL usa snake_case)
          const normalized = result.clients.map(c => ({
            ...c,
            hours: c.service_duration || c.hours, // fallback para campo antigo
            address: c.address || ""
          }));
          setClients(normalized);
        }
      } catch (error) {
        console.error("Erro ao carregar clientes:", error);
        // Fallback para Firestore se PostgreSQL falhar
        try {
          const snap = await getDocs(collection(db, "clients"));
          setClients(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (fbError) {
          console.error("Erro no fallback Firestore:", fbError);
        }
      }
    };
    loadClients();
  }, []);

  // Verifica se semana é atual ou passada (não futura)
  const isWeekImportable = useMemo(() => {
    const now = new Date();
    const currentMonday = startOfWeek(now, { weekStartsOn: 1 });
    return weekStart <= currentMonday;
  }, [weekStart]);

  // Importa dados do calendário para preencher o fechamento
  const importFromCalendar = async () => {
    if (!isWeekImportable) return;
    setImportingCalendar(true);
    try {
      const wsStr = fnsFormat(weekStart, "yyyy-MM-dd");
      const data = await callXeroFunction('getCollaboratorWeekImport',
        { uid: targetUid, weekStart: wsStr }, { method: 'GET' });

      if (!data.success || !data.days) {
        setImportToast(t(language, "noCalendarData") || "No calendar data found for this week");
        return false;
      }

      const newReport = {};
      const newWorkers = {};
      let totalJobs = 0;

      for (const dayName of days) {
        const dayData = data.days[dayName];
        if (!dayData || dayData.jobs.length === 0) {
          newReport[dayName] = [];
          newWorkers[dayName] = 1;
          continue;
        }
        // Auto-import: filtrar só jobs do time registrado da colaboradora
        const memberTeam = (dayData.teams || [])[0];
        const dayJobs = memberTeam
          ? dayData.jobs.filter(j => j.team === memberTeam)
          : dayData.jobs;
        newReport[dayName] = dayJobs.map(j => ({
          client: j.clientName,
          originalHours: j.serviceDuration || "2:00",
          editedHours: null,
          justification: null,
          photoUrl: null,
          uploadedFiles: null,
          __manual: false,
          __address: j.address || null
        }));
        newWorkers[dayName] = dayData.teamSize || 1;
        totalJobs += dayJobs.length;
      }

      if (totalJobs === 0) {
        setImportToast(t(language, "noCalendarData") || "No calendar data found for this week");
        return false;
      }

      setWeeklyReport(prev => ({ ...prev, ...newReport }));
      setNumWorkers(prev => ({ ...prev, ...newWorkers }));
      setImportToast(`${totalJobs} ${t(language, "jobsImported") || "jobs imported from calendar"}`);
      return true;
    } catch (err) {
      console.error("Error importing from calendar:", err);
      setImportToast(t(language, "importError") || "Error importing calendar data");
      return false;
    } finally {
      setImportingCalendar(false);
    }
  };

  // Abre modal de comparação (admin only) — busca calendário sem sobrescrever
  const openCompareModal = async () => {
    setCompareModal({ show: true, calendarData: null, loading: true });
    try {
      const wsStr = fnsFormat(weekStart, "yyyy-MM-dd");
      const data = await callXeroFunction('getCollaboratorWeekImport',
        { uid: targetUid, weekStart: wsStr }, { method: 'GET' });
      setCompareModal({ show: true, calendarData: data.success ? data.days : null, loading: false });
    } catch (err) {
      console.error("Error fetching calendar data for compare:", err);
      setCompareModal({ show: true, calendarData: null, loading: false });
    }
  };

  useEffect(() => {
    // 7.0) ao navegar para outra semana, redefina os estados
    setWeeklyReport(days.reduce((o, d) => ({ ...o, [d]: [] }), {}));
    setNumWorkers(days.reduce((o, d) => ({ ...o, [d]: 1 }), {}));
    setLoadingInit(true);

    // 7.1) então busque o relatório daquela semana
    const refDoc = doc(db, "weeklyReports", reportId);
    getDoc(refDoc)
      .then(snap => {
        if (snap.exists()) {
          const data = snap.data();
          // — preenche apenas os dias que existem no banco
          setWeeklyReport(prev => ({ ...prev, ...data.days }));
          setNumWorkers(prev => ({ ...prev, ...data.numWorkers }));
        }
      })
      .finally(() => setLoadingInit(false));
  }, [reportId]); // eslint-disable-line react-hooks/exhaustive-deps

  // calcula totais diários (trabalho, almoço, trajeto)
  const calculateTotal = async day => {
    setLoadingTotals(l => ({ ...l, [day]: true }));
    const entries = weeklyReport[day] || [];
    const workers = Math.max(1, numWorkers[day] || 1);

    // horas de trabalho
    let workMin = 0;
    for (let e of entries) {
      if (e.editedHours) {
        // quando a usuária editou, originalHours → horas por pessoa, então multiplica
        workMin += timeToMinutes(e.editedHours) * workers;
      } else if (e.__manual) {
        // entrada manual: o valor digitado já é POR PESSOA, então multiplica
        workMin += timeToMinutes(e.originalHours) * workers;
      } else {
        // cliente “pré-cadastrado”: originalHours é total da casa, soma 1 vez
        workMin += timeToMinutes(e.originalHours);
      }
    }
    setWorkMinDay(w => ({ ...w, [day]: workMin })); // (removida duplicidade)

    // minuto de descanso pago (breaks pagos conforme limiares)
    // ≥ 2h → +10 min
    // ≥ 6h → +10 min adicional (total 20 min)
    // ≥ 10h → +10 min adicional (total 30 min)
    // IMPORTANTE: calcular break sobre tempo POR PESSOA, não sobre tempo total
    const perPersonForBreak = Math.round(workMin / workers);
    let paidBreak = 0;
    if (perPersonForBreak >= 120) paidBreak += 10;  // ≥ 2h
    if (perPersonForBreak >= 360) paidBreak += 10;  // ≥ 6h
    if (perPersonForBreak >= 600) paidBreak += 10;  // ≥ 10h
    setLunchMinDay(l => ({ ...l, [day]: paidBreak }));

    // minuto de trajeto
    // PASSO 1: coleta todos os trajetos e calcula total provisório
    // IMPORTANTE: Prioriza __address (editado) sobre endereço cadastrado
    const travelData = [];
    for (let i = 0; i < entries.length - 1; i++) {
      // Origem: prioriza __address, senão busca do cadastro
      const o = entries[i].__address
        ? entries[i].__address
        : clients.find(c => c.name === entries[i].client)?.address;
      // Destino: prioriza __address, senão busca do cadastro
      const d = entries[i + 1].__address
        ? entries[i + 1].__address
        : clients.find(c => c.name === entries[i + 1].client)?.address;
      if (o && d) {
        // Usa getTravelMinutes com cache para economizar custos da API
        const travelTimeMinutes = await getTravelMinutes(o, d);
        travelData.push({ index: i, travelTime: travelTimeMinutes });
      }
    }

    // PASSO 2: calcula total provisório (sem o primeiro trajeto se < 5 min)
    let travelWithoutFirst = 0;
    for (let j = 0; j < travelData.length; j++) {
      const { index, travelTime } = travelData[j];
      if (index === 0) continue; // pula o primeiro por enquanto

      const rounded = roundTravelMinute(travelTime);
      travelWithoutFirst += rounded;
    }

    // PASSO 3: aplica regra do primeiro trajeto
    let travel = 0;
    for (let j = 0; j < travelData.length; j++) {
      const { index, travelTime } = travelData[j];

      if (index === 0) {
        // Primeiro trajeto: só conta se total ficar < 5 min
        if (travelTime < 5 && travelTime > 0) {
          const wouldBeTotal = travelTime + travelWithoutFirst;
          if (wouldBeTotal < 5) {
            travel += travelTime;
          }
          // Senão não soma nada
        } else if (travelTime >= 5) {
          // >= 5 min: sempre arredonda e conta
          const rounded = roundTravelMinute(travelTime);
          travel += rounded;
        }
      } else {
        // Do segundo trajeto em diante: só conta se >= 5 min
        const rounded = roundTravelMinute(travelTime);
        travel += rounded;
      }
    }

    // PASSO 4: se total > 5 min, arredonda para múltiplo de 5 para baixo
    if (travel > 5) {
      travel = floorTo5(travel);
    }

    setTravelMinDay(t => ({ ...t, [day]: travel }));

    // total exibido (somente horas por pessoa, SEM break pago para não confundir)
    const perPerson = Math.round(workMin / workers);
    setTotals(t => ({ ...t, [day]: minutesToTime(perPerson) }));
    setLoadingTotals(l => ({ ...l, [day]: false }));
  };

  // dispara recalculo APENAS dos dias que mudaram (não a semana inteira!)
  useEffect(() => {
    // Aguarda Google Maps e lista de clientes carregarem
    if (!isLoaded || clients.length === 0) return;

    const prevReport = prevWeeklyReportRef.current;

    // Detecta quais dias mudaram comparando com a versão anterior
    const changedDays = days.filter(day => {
      // Se numWorkers mudou, precisa recalcular
      // Se o array de clientes mudou (tamanho ou conteúdo), precisa recalcular
      const currClients = weeklyReport[day] || [];
      const prevClients = prevReport[day] || [];

      // Compara tamanho do array
      if (currClients.length !== prevClients.length) return true;

      // Compara cada cliente (nome, horas, endereço, etc)
      return currClients.some((c, i) => {
        const p = prevClients[i];
        return !p ||
               c.client !== p.client ||
               c.originalHours !== p.originalHours ||
               c.editedHours !== p.editedHours ||
               c.__address !== p.__address;
      });
    });

    // Recalcula apenas os dias que mudaram
    changedDays.forEach(d => calculateTotal(d));

    // Atualiza ref para próxima comparação
    prevWeeklyReportRef.current = weeklyReport;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeklyReport, numWorkers, isLoaded, clients]);

  // Handlers de UI

  const addClient = (day, opt) => {
    if (opt?.id) {
      // encontra o cliente completo para ler o schedule daquele dia
      const found = clients.find(c => c.id === opt.id) || {};
      // prioriza schedule[day], senão usa hours padrão
      const schedH = found.schedule?.[day];
      const origH = schedH ?? found.hours;
      setWeeklyReport(w => ({
        ...w,
        [day]: [
          ...w[day],
          {
            client: opt.name,
            originalHours: origH,
            editedHours: null,
            __manual: false
          }
        ]
      }));
    } else {
      setNewClient({
        day,
        name: searchQuery[day] || "",
        address: "",
        hours: "",
        visible: true
      });
    }
  };

  const removeClient = (day, i) => {
    setWeeklyReport(w => {
      const arr = [...w[day]];
      arr.splice(i, 1);
      return { ...w, [day]: arr };
    });
  };

  // --- Drag & Drop para reordenação de clientes ---
  const reorderClient = (day, fromIndex, toIndex) => {
    setWeeklyReport(w => {
      const arr = [...w[day]];
      const [item] = arr.splice(fromIndex, 1);
      arr.splice(toIndex, 0, item);
      return { ...w, [day]: arr };
    });
  };

  const dragRef = useRef({ day: null, index: null });
  const [dragOverState, setDragOverState] = useState({ day: null, index: null });
  const touchDragRef = useRef(null);

  // Desktop: HTML5 Drag & Drop
  const onDragStart = (e, day, index) => {
    dragRef.current = { day, index };
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');
    requestAnimationFrame(() => {
      const entry = e.target.closest('[data-entry-index]');
      if (entry) entry.style.opacity = '0.4';
    });
  };

  const onDragEnd = (e) => {
    const entry = e.target.closest('[data-entry-index]');
    if (entry) entry.style.opacity = '1';
    dragRef.current = { day: null, index: null };
    setDragOverState({ day: null, index: null });
  };

  const onDragOver = (e, day, index) => {
    e.preventDefault();
    if (dragRef.current.day !== day) return;
    if (dragOverState.day !== day || dragOverState.index !== index) {
      setDragOverState({ day, index });
    }
  };

  const onDrop = (e, day, index) => {
    e.preventDefault();
    const fromIndex = dragRef.current.index;
    if (fromIndex !== null && fromIndex !== index) {
      reorderClient(day, fromIndex, index);
    }
    dragRef.current = { day: null, index: null };
    setDragOverState({ day: null, index: null });
  };

  // Mobile: Touch Drag & Drop
  // Handlers como refs para poder registrar no DOM com { passive: false }
  const touchMoveHandler = useRef(null);
  const touchEndHandler = useRef(null);

  const onTouchDragStart = (e, day, index) => {
    const touch = e.touches[0];
    const entry = e.currentTarget.closest('[data-entry-index]');
    const rect = entry.getBoundingClientRect();

    const ghost = entry.cloneNode(true);
    Object.assign(ghost.style, {
      position: 'fixed', top: rect.top + 'px', left: rect.left + 'px',
      width: rect.width + 'px', opacity: '0.85', zIndex: '9999',
      pointerEvents: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
      borderRadius: '6px', transform: 'scale(1.02)'
    });
    document.body.appendChild(ghost);
    entry.style.opacity = '0.3';

    touchDragRef.current = {
      day, index, ghost, entry,
      startY: touch.clientY, offsetY: rect.top, currentOver: index
    };

    // Registra listeners no document com { passive: false } para poder preventDefault
    touchMoveHandler.current = (ev) => {
      ev.preventDefault();
      const ref = touchDragRef.current;
      if (!ref) return;
      const t = ev.touches[0];
      ref.ghost.style.top = (ref.offsetY + t.clientY - ref.startY) + 'px';

      const target = document.elementFromPoint(t.clientX, t.clientY);
      const entryEl = target?.closest('[data-entry-index]');
      if (entryEl && entryEl.dataset.entryDay === ref.day) {
        const overIdx = parseInt(entryEl.dataset.entryIndex);
        if (overIdx !== ref.currentOver) {
          ref.currentOver = overIdx;
          setDragOverState({ day: ref.day, index: overIdx });
        }
      }
    };

    touchEndHandler.current = () => {
      const ref = touchDragRef.current;
      if (!ref) return;
      ref.ghost.remove();
      ref.entry.style.opacity = '1';
      if (ref.index !== ref.currentOver) {
        reorderClient(ref.day, ref.index, ref.currentOver);
      }
      touchDragRef.current = null;
      setDragOverState({ day: null, index: null });
      document.removeEventListener('touchmove', touchMoveHandler.current);
      document.removeEventListener('touchend', touchEndHandler.current);
    };

    document.addEventListener('touchmove', touchMoveHandler.current, { passive: false });
    document.addEventListener('touchend', touchEndHandler.current);
  };

  const openEdit = (day, i) => {
    const e = weeklyReport[day][i];

    // Carregar endereço atual (prioriza __address, senão busca do cliente cadastrado)
    let currentAddress = e.__address || "";
    if (!currentAddress && !e.__manual) {
      const clientData = clients.find(c => c.name === e.client);
      currentAddress = clientData?.address || "";
    }

    setEditModal({ day, index: i, ...e, __address: currentAddress });
  };

  // Salvar edição com opção de retornar se sucesso
  const handleEditAndNavigate = async (navigateTo) => {
    const ev = new Event("submit");
    ev.preventDefault = () => {};

    const m = editModal;
    const workers = numWorkers[m.day] || 1;
    const origMin = timeToMinutes(m.originalHours);
    const edtMin = timeToMinutes(m.editedHours || "00:00") * workers;
    if (edtMin > origMin && !m.justification?.trim()) {
      alert(t(language, "justificationRequired"));
      return false;
    }

    // Computar mudanças para histórico
    const previous = weeklyReport[m.day][m.index];
    const changes = [];
    const prevTime = previous.editedHours || previous.originalHours;

    if (m.editedHours && m.editedHours !== previous.editedHours) {
      changes.push(`Horas alteradas ${m.client} de ${prevTime} para ${m.editedHours}`);
    }
    if (m.justification && m.justification !== previous.justification) {
      changes.push(`Justificativa: ${m.justification}`);
    }
    // Atualizar estado local
    setWeeklyReport(w => {
      const arr = [...w[m.day]];
      arr[m.index] = {
        client: m.client,
        originalHours: m.originalHours,
        editedHours: m.editedHours ?? null,
        justification: m.justification ?? null,
        __manual: m.__manual ?? null,
        __address: m.__address ?? null
      };
      return { ...w, [m.day]: arr };
    });

    // Gravar histórico no Firestore
    try {
      const refDoc = doc(db, "weeklyReports", reportId);
      const currentDoc = await getDoc(refDoc);

      if (currentDoc.exists()) {
        const currentData = currentDoc.data();
        const updatedDays = { ...currentData.days };
        if (!updatedDays[m.day]) {
          updatedDays[m.day] = [];
        }
        updatedDays[m.day][m.index] = {
          client: m.client,
          originalHours: m.originalHours,
          editedHours: m.editedHours ?? null,
          justification: m.justification ?? null,
          __manual: m.__manual ?? null,
          __address: m.__address ?? null
        };

        await updateDoc(refDoc, {
          days: updatedDays,
          history: arrayUnion({ when: Timestamp.now(), user: nome, changes })
        });
      } else {
        // Documento não existe ainda (ex: dados auto-importados) — criar
        const newDays = {};
        days.forEach(d => {
          newDays[d] = (weeklyReport[d] || []).map(e => ({
            client: e.client, originalHours: e.originalHours,
            editedHours: e.editedHours ?? null, justification: e.justification ?? null,
            photoUrl: e.photoUrl ?? null, uploadedFiles: e.uploadedFiles ?? null,
            __manual: e.__manual ?? null, __address: e.__address ?? null
          }));
        });
        // Aplicar a edição atual
        newDays[m.day][m.index] = {
          client: m.client, originalHours: m.originalHours,
          editedHours: m.editedHours ?? null, justification: m.justification ?? null,
          __manual: m.__manual ?? null, __address: m.__address ?? null
        };
        await setDoc(refDoc, {
          uid: targetUid, slug, weekStart: fnsFormat(weekStart, "yyyy-MM-dd"),
          days: newDays, numWorkers,
          createdAt: Timestamp.now(), updatedAt: Timestamp.now(),
          history: [{ when: Timestamp.now(), user: nome, changes }]
        });
      }

      setEditModal(null);

      if (navigateTo) {
        navigate(navigateTo);
      }
      return true;
    } catch (err) {
      console.error("Erro ao salvar:", err);
      alert("Erro ao salvar. Tente novamente.");
      return false;
    }
  };

  // Salvar edição normal (sem navegar)
  const handleEdit = async ev => {
    ev.preventDefault();
    await handleEditAndNavigate(null);
  };

  // totais semanais
  const totalWorkWeek = days.reduce(
    (s, d) => s + (workMinDay[d] || 0) / Math.max(1, numWorkers[d] || 1),
    0
  );
  const totalLunchWeek = Object.values(lunchMinDay).reduce((a, b) => a + b, 0);
  const totalTravelWeek = Object.values(travelMinDay).reduce((a, b) => a + b, 0);
  const weekWorkMin = Math.round(totalWorkWeek);
  const weekWorkStr = minutesToTime(weekWorkMin);
  const weekTotalMin = weekWorkMin + totalLunchWeek + totalTravelWeek;
  const weekTotalStr = minutesToTime(weekTotalMin);

  // 11) salvar no Firestore (mantém antigo comportamento)
  const saveReport = async () => {
    const ts = Timestamp.now();
    const docRef = doc(db, "weeklyReports", reportId);

    // Sanitizar dados, removendo valores undefined
    const sanitizedDays = {};
    days.forEach(day => {
      sanitizedDays[day] = (weeklyReport[day] || []).map(e => ({
        client: e.client,
        originalHours: e.originalHours,
        editedHours: e.editedHours ?? null,
        justification: e.justification ?? null,
        photoUrl: e.photoUrl ?? null,
        uploadedFiles: e.uploadedFiles ?? null,
        __manual: e.__manual ?? null,
        __address: e.__address ?? null
      }));
    });

    // Sanitizar valores para evitar undefined (Firestore rejeita)
    const safeTotals = {};
    const safeLunch = {};
    const safeTravel = {};
    const safeWorkers = {};
    days.forEach(d => {
      safeTotals[d] = totals[d] || "00:00";
      safeLunch[d] = lunchMinDay[d] || 0;
      safeTravel[d] = travelMinDay[d] || 0;
      safeWorkers[d] = numWorkers[d] || 1;
    });

    const payload = {
      uid: targetUid,
      slug,
      weekStart: fnsFormat(weekStart, "yyyy-MM-dd"),
      days: sanitizedDays,
      numWorkers: safeWorkers,
      totalHoursPerDay: safeTotals,
      lunchMinDay: safeLunch,
      travelMinDay: safeTravel,
      weekWorkMin: weekWorkMin || 0,
      weekWorkHours: weekWorkStr || "00:00",
      weekTotalHours: weekTotalStr || "00:00",
      updatedAt: ts
    };

    const snap = await getDoc(docRef);
    if (!snap.exists()) {
      await setDoc(docRef, {
        ...payload,
        createdAt: ts,
        history: [{ when: ts, user: nome, changes: [] }]
      });
    } else {
      await updateDoc(docRef, {
        ...payload,
        history: arrayUnion({ when: ts, user: nome, changes: [] })
      });
    }
    alert(t(language, "closingSaved"));
  };

  // carregamento inicial / erros
  if (loadingInit || loadError || !isLoaded) {
    return (
      <Container className="text-center py-5">
        {loadError ? <p>{t(language, "errorLoadingMaps")}</p> : <Spinner animation="border" />}
      </Container>
    );
  }

  // 12) renderização
  return (
    <Container className="py-4">
      {/* Cabeçalho */}
      <Card className="mb-3 border-0 shadow-sm">
        <Card.Body className="d-flex align-items-center py-3">
          <Button
            variant="outline-secondary"
            size="sm"
            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: '38px', height: '38px', padding: 0 }}
            onClick={() =>
              navigate(
                `${basePath}?weekStart=${fnsFormat(subWeeks(hoje, 1), "yyyy-MM-dd")}`
              )
            }
          >
            &larr;
          </Button>
          <div className="flex-grow-1 text-center">
            <h5 className="mb-0">{t(language, "weekClosing")}</h5>
            <small className="text-muted">{labelWeek}</small>
          </div>
          <Button
            variant="outline-secondary"
            size="sm"
            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: '38px', height: '38px', padding: 0 }}
            onClick={() =>
              navigate(
                `${basePath}?weekStart=${fnsFormat(addWeeks(hoje, 1), "yyyy-MM-dd")}`
              )
            }
          >
            &rarr;
          </Button>
        </Card.Body>
      </Card>

      {/* Botão Re-importar + Compare (admin) + Toast */}
      {isWeekImportable && (
        <div className="d-flex align-items-center gap-2 mb-3">
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={importingCalendar}
            onClick={() => {
              if (window.confirm(t(language, "confirmReimport") || "Replace current data with calendar data?")) {
                importFromCalendar();
              }
            }}
          >
            {importingCalendar
              ? <><Spinner animation="border" size="sm" className="me-1" /> {t(language, "importing") || "Importing..."}</>
              : <>{t(language, "importFromCalendar") || "Import from Calendar"}</>
            }
          </Button>
          {user?.isAdmin && (
            <Button
              variant="outline-info"
              size="sm"
              disabled={compareModal.loading}
              onClick={openCompareModal}
            >
              {compareModal.loading
                ? <Spinner animation="border" size="sm" />
                : <>{t(language, "compareCalendar") || "Compare"}</>
              }
            </Button>
          )}
          {importToast && (
            <small className="text-muted fst-italic">{importToast}</small>
          )}
        </div>
      )}

      {/* Resumo Semanal */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <h6 className="mb-3">{t(language, "weeklySummary")}</h6>
          <div className="d-flex flex-wrap gap-3">
            <div>
              <small className="text-muted d-block mb-1">{t(language, "hoursWorked")}</small>
              <span className="badge rounded-pill" style={{ backgroundColor: '#e7f1ff', color: '#0d6efd', fontSize: '1em', padding: '6px 14px' }}>
                {weekWorkStr}
              </span>
            </div>
            <div>
              <small className="text-muted d-block mb-1">{t(language, "totalLunch")}</small>
              <span className="badge rounded-pill" style={{ backgroundColor: '#e0f7fa', color: '#0891b2', fontSize: '1em', padding: '6px 14px' }}>
                {minutesToTime(totalLunchWeek)}
              </span>
            </div>
            <div>
              <small className="text-muted d-block mb-1">{t(language, "totalTravel")}</small>
              <span className="badge rounded-pill" style={{ backgroundColor: '#fff3e0', color: '#e65100', fontSize: '1em', padding: '6px 14px' }}>
                {minutesToTime(totalTravelWeek)}
              </span>
            </div>
            <div>
              <small className="text-muted d-block mb-1">{t(language, "totalClosing")}</small>
              <span className="badge rounded-pill bg-success" style={{ fontSize: '1em', padding: '6px 14px' }}>
                {weekTotalStr}
              </span>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Botões */}
      <div className="d-flex justify-content-between mb-3">
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={() => navigate(
            user?.isAdmin && paramUid ? `/admin/users/${paramUid}/profile` : "/"
          )}>
            ← {t(language, "back")}
          </Button>
          <Button variant="outline-info" size="sm" onClick={() => navigate("/tutorial")}>
            {t(language, "tutorialHelp")}
          </Button>
        </div>
        <Button variant="success" onClick={saveReport}>
          {t(language, "saveClosing")}
        </Button>
      </div>

      {/* Cards diários */}
      <Row className="g-4">
        {days.map(day => (
          <Col md={6} lg={4} key={day}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Header className="d-flex justify-content-between align-items-center" style={{ backgroundColor: '#f0f4f8' }}>
                <strong style={{ fontSize: '1rem' }}>{day}</strong>
                <InputGroup style={{ width: "7rem" }}>
                  <FormControl
                    type="text"
                    value={numWorkers[day] || ""}
                    placeholder="1"
                    onChange={e => {
                      const val = e.target.value;
                      if (val === "") {
                        setNumWorkers(n => ({ ...n, [day]: "" }));
                      } else {
                        const num = parseInt(val) || 1;
                        setNumWorkers(n => ({ ...n, [day]: Math.max(1, num) }));
                      }
                    }}
                    onBlur={e => {
                      if (e.target.value === "" || parseInt(e.target.value) < 1) {
                        setNumWorkers(n => ({ ...n, [day]: 1 }));
                      }
                    }}
                  />
                  <InputGroup.Text>👥</InputGroup.Text>
                </InputGroup>
              </Card.Header>
              <Card.Body>
                {/* Busca de cliente + adicionar manual */}
                <Combobox onChange={opt => addClient(day, opt)} value={null}>
                  <div className="position-relative mb-2">
                    <Combobox.Input
                      className="form-control"
                      placeholder={t(language, "searchClient")}
                      onChange={e =>
                        setSearchQuery(q => ({ ...q, [day]: e.target.value }))
                      }
                    />
                    <Combobox.Options
                      className="list-group position-absolute w-100 mt-1 bg-white border rounded shadow-sm"
                      style={{ zIndex: 1000, maxHeight: "20vh", overflowY: "auto" }}
                    >
                      {clients
                        .filter(c =>
                          c.name
                            .toLowerCase()
                            .includes((searchQuery[day] || "").toLowerCase())
                        )
                        .map(c => (
                          <Combobox.Option key={c.id} value={c}>
                            {c.name}
                          </Combobox.Option>
                        ))}
                      {!!(searchQuery[day] || "").trim() && (
                        <Combobox.Option value={{ id: null }}>
                          ➕ Adicionar “{searchQuery[day]}”
                        </Combobox.Option>
                      )}
                    </Combobox.Options>
                  </div>
                </Combobox>

                {/* Formulário inline para adicionar manual */}
                {newClient.visible && newClient.day === day && (
                  <Form className="p-2 mb-3 bg-light border rounded">
                    {/* Nome */}
                    <Form.Group className="mb-2">
                      <Form.Label>{t(language, "name")}</Form.Label>
                      <Form.Control
                        type="text"
                        value={newClient.name}
                        onChange={e =>
                          setNewClient(n => ({ ...n, name: e.target.value }))
                        }
                      />
                    </Form.Group>

                    {/* Endereço com Autocomplete restrito a Auckland */}
                    <Form.Group className="mb-2 position-relative">
                      <Form.Label>{t(language, "addressAuckland")}</Form.Label>
                      <Form.Control
                        type="text"
                        value={newClient.address}
                        placeholder={t(language, "startTyping")}
                        onChange={e => {
                          const val = e.target.value;
                          setNewClient(n => ({
                            ...n,
                            address: val,
                            addressValid: false
                          }));
                          if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
                          if (addressService && val && val.length >= 3) {
                            if (!addressSessionTokenRef.current && window.google?.maps?.places) {
                              addressSessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
                            }
                            addressDebounceRef.current = setTimeout(() => {
                              addressService.getPlacePredictions(
                                {
                                  input: val,
                                  componentRestrictions: { country: "nz" },
                                  locationRestriction: aklBounds,
                                  sessionToken: addressSessionTokenRef.current
                                },
                                (preds, status) => {
                                  setAddressSuggestions(
                                    status === "OK" ? preds : []
                                  );
                                }
                              );
                            }, 300);
                          } else {
                            setAddressSuggestions([]);
                          }
                        }}
                      />
                      {addressSuggestions.length > 0 && (
                        <div
                          className="list-group position-absolute w-100"
                          style={{
                            zIndex: 2000,
                            maxHeight: "12rem",
                            overflowY: "auto"
                          }}
                        >
                          {addressSuggestions.map((s, i) => (
                            <button
                              type="button"
                              key={i}
                              className="list-group-item list-group-item-action"
                              onClick={() => {
                                setNewClient(n => ({
                                  ...n,
                                  address: s.description,
                                  addressValid: true
                                }));
                                setAddressSuggestions([]);
                                addressSessionTokenRef.current = null;
                              }}
                            >
                              {s.description}
                            </button>
                          ))}
                        </div>
                      )}
                    </Form.Group>

                    {/* Horas */}
                    <Form.Group className="mb-2">
                      <Form.Label>{t(language, "hours")}</Form.Label>
                      <Form.Control
                        type="text"
                        value={newClient.hours}
                        onChange={e =>
                          setNewClient(n => ({ ...n, hours: e.target.value }))
                        }
                      />
                    </Form.Group>

                    {/* Botões */}
                    <div className="text-end">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() =>
                          setNewClient({
                            day: null,
                            name: "",
                            address: "",
                            hours: "",
                            visible: false
                          })
                        }
                      >
                        {t(language, "cancel")}
                      </Button>{" "}
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          if (!newClient.name.trim()) {
                            return alert(t(language, "fillClientName"));
                          }
                          if (!newClient.addressValid) {
                            return alert(t(language, "selectValidAddress"));
                          }
                          if (!/^\d{1,2}:\d{2}$/.test(newClient.hours)) {
                            return alert(t(language, "hoursFormat"));
                          }
                          // adiciona o cliente com endereço normalizado
                          const normalizedAddress = formatAddress(newClient.address.trim());
                          setWeeklyReport(w => ({
                            ...w,
                            [newClient.day]: [
                              ...w[newClient.day],
                              {
                                client: newClient.name.trim(),
                                originalHours: newClient.hours,
                                editedHours: null,
                                __manual: true,
                                __address: normalizedAddress
                              }
                            ]
                          }));
                          setNewClient({
                            day: null,
                            name: "",
                            address: "",
                            hours: "",
                            visible: false
                          });
                        }}
                      >
                        {t(language, "add")}
                      </Button>
                    </div>
                  </Form>
                )}

                {/* Entradas de clientes */}
                {weeklyReport[day].map((e, i) => {
                  const dispMin = e.editedHours
                    ? timeToMinutes(e.editedHours)
                    : e.__manual
                    ? timeToMinutes(e.originalHours)
                    : timeToMinutes(e.originalHours) / numWorkers[day];
                  const dispStr = minutesToTime(Math.round(dispMin));

                  let borderColor = '#dee2e6';
                  if (e.editedHours) {
                    const orig = timeToMinutes(e.originalHours);
                    const edt = timeToMinutes(e.editedHours) * numWorkers[day];
                    if (edt > orig) borderColor = '#dc3545';
                    else if (edt < orig) borderColor = '#198754';
                  }

                  let clientAddress = "";
                  if (user?.isAdmin) {
                    if (e.__address) {
                      clientAddress = formatAddress(e.__address);
                    } else if (!e.__manual) {
                      clientAddress = formatAddress(clients.find(c => c.name === e.client)?.address || "");
                    }
                  }

                  return (
                    <div
                      key={i}
                      data-entry-index={i}
                      data-entry-day={day}
                      className="d-flex align-items-center py-2 px-2 mb-1 rounded"
                      style={{
                        backgroundColor: dragOverState.day === day && dragOverState.index === i && dragRef.current.index !== i ? '#e3f2fd' : '#f8f9fa',
                        borderLeft: `3px solid ${borderColor}`,
                        borderTop: dragOverState.day === day && dragOverState.index === i && dragRef.current.index !== i ? '2px solid #0d6efd' : '2px solid transparent',
                        transition: 'background-color 0.15s, border-top 0.1s'
                      }}
                      onDragOver={(ev) => onDragOver(ev, day, i)}
                      onDrop={(ev) => onDrop(ev, day, i)}
                    >
                      {/* Drag handle */}
                      {weeklyReport[day].length > 1 && (
                        <div
                          draggable
                          className="me-2 d-flex align-items-center"
                          style={{ cursor: 'grab', touchAction: 'none', color: '#adb5bd', padding: '4px 2px', userSelect: 'none', fontSize: '1.1rem' }}
                          onDragStart={(ev) => onDragStart(ev, day, i)}
                          onDragEnd={onDragEnd}
                          onTouchStart={(ev) => onTouchDragStart(ev, day, i)}
                          title="Arrastar para reordenar"
                        >
                          &#10303;
                        </div>
                      )}
                      <div className="flex-grow-1 me-2" style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-truncate fw-medium" style={{ fontSize: '0.9rem' }}>{e.client}</span>
                          <span className="badge rounded-pill flex-shrink-0" style={{ backgroundColor: '#e7f1ff', color: '#0d6efd', fontSize: '0.8em' }}>{dispStr}</span>
                        </div>
                        {user?.isAdmin && clientAddress && (
                          <div className="text-muted text-truncate" style={{ fontSize: '0.75rem' }}>{clientAddress}</div>
                        )}
                      </div>
                      <div className="d-flex gap-1 flex-shrink-0">
                        <Button size="sm" variant="outline-secondary" className="px-2 py-1" onClick={() => openEdit(day, i)} style={{ minWidth: '34px', minHeight: '34px' }}>
                          ✏️
                        </Button>
                        <Button size="sm" variant="outline-danger" className="px-2 py-1" onClick={() => removeClient(day, i)} style={{ minWidth: '34px', minHeight: '34px' }}>
                          🗑️
                        </Button>
                      </div>
                    </div>
                  );
                })}

                {/* Totais diários */}
                <div className="d-flex flex-wrap gap-2 mt-3 pt-2 border-top">
                  <div className="d-flex align-items-center gap-1">
                    <small className="text-muted">{t(language, "hoursWorked")}:</small>
                    {loadingTotals[day] ? <Spinner animation="border" size="sm" /> : (
                      <span className="badge rounded-pill" style={{ backgroundColor: '#e7f1ff', color: '#0d6efd' }}>{totals[day]}</span>
                    )}
                  </div>
                  {user?.isAdmin && (
                    <>
                      <div className="d-flex align-items-center gap-1">
                        <small className="text-muted">{t(language, "totalTravel")}:</small>
                        {loadingTotals[day] ? <Spinner animation="border" size="sm" /> : (
                          <span className="badge rounded-pill" style={{ backgroundColor: '#fff3e0', color: '#e65100' }}>{minutesToTime(travelMinDay[day] || 0)}</span>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-1">
                        <small className="text-muted">Breaks:</small>
                        {loadingTotals[day] ? <Spinner animation="border" size="sm" /> : (
                          <span className="badge rounded-pill" style={{ backgroundColor: '#e0f7fa', color: '#0891b2' }}>{minutesToTime(lunchMinDay[day] || 0)}</span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Modal de edição */}
      <Modal show={!!editModal} onHide={() => {
        setEditModal(null);
        setAddressSuggestions([]);
      }}>
        <Modal.Header closeButton>
          <Modal.Title>{t(language, "edit")} {editModal?.client}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEdit}>
            <Form.Group className="mb-3 position-relative">
              <Form.Label>Cliente</Form.Label>
              <Form.Control
                type="text"
                placeholder="Pesquisar cliente..."
                value={editModal?.client || ""}
                onChange={e =>
                  setEditModal(m => ({ ...m, client: e.target.value, showClientList: true }))
                }
              />
              {editModal?.showClientList && editModal?.client && (
                <div
                  className="list-group position-absolute w-100"
                  style={{
                    zIndex: 2000,
                    maxHeight: "12rem",
                    overflowY: "auto"
                  }}
                >
                  {clients
                    .filter(c =>
                      c.name.toLowerCase().includes((editModal?.client || "").toLowerCase())
                    )
                    .map(c => (
                      <button
                        type="button"
                        key={c.id}
                        className="list-group-item list-group-item-action"
                        onClick={() => {
                          setEditModal(m => ({
                            ...m,
                            client: c.name,
                            originalHours: c.hours,
                            editedHours: null,
                            showClientList: false,
                            __address: c.address || ""
                          }));
                        }}
                      >
                        {c.name}
                      </button>
                    ))}
                </div>
              )}
            </Form.Group>

            {/* Campo de Endereço com Autocomplete */}
            <Form.Group className="mb-3 position-relative">
              <Form.Label>{t(language, "address") || "Endereço"}</Form.Label>
              <Form.Control
                type="text"
                placeholder={t(language, "startTyping") || "Comece a digitar..."}
                value={editModal?.__address || ""}
                onChange={e => {
                  const val = e.target.value;
                  setEditModal(m => ({ ...m, __address: val }));
                  if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
                  if (addressService && val && val.length >= 3) {
                    if (!addressSessionTokenRef.current && window.google?.maps?.places) {
                      addressSessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
                    }
                    addressDebounceRef.current = setTimeout(() => {
                      addressService.getPlacePredictions(
                        {
                          input: val,
                          componentRestrictions: { country: "nz" },
                          locationRestriction: aklBounds,
                          sessionToken: addressSessionTokenRef.current
                        },
                        (preds, status) => {
                          setAddressSuggestions(status === "OK" ? preds : []);
                        }
                      );
                    }, 300);
                  } else {
                    setAddressSuggestions([]);
                  }
                }}
              />
              {addressSuggestions.length > 0 && (
                <div
                  className="list-group position-absolute w-100"
                  style={{
                    zIndex: 2000,
                    maxHeight: "12rem",
                    overflowY: "auto"
                  }}
                >
                  {addressSuggestions.map((s, i) => (
                    <button
                      type="button"
                      key={i}
                      className="list-group-item list-group-item-action"
                      onClick={() => {
                        setEditModal(m => ({ ...m, __address: s.description }));
                        setAddressSuggestions([]);
                        addressSessionTokenRef.current = null;
                      }}
                    >
                      {s.description}
                    </button>
                  ))}
                </div>
              )}
            </Form.Group>

            <Form.Control
              className="mb-3"
              type="text"
              placeholder={t(language, "hours")}
              value={editModal?.editedHours || ""}
              onChange={e =>
                setEditModal(m => ({ ...m, editedHours: e.target.value }))
              }
            />
            <Form.Control
              className="mb-3"
              as="textarea"
              rows={3}
              placeholder={t(language, "justification")}
              value={editModal?.justification || ""}
              onChange={e =>
                setEditModal(m => ({ ...m, justification: e.target.value }))
              }
            />
            <div className="d-flex justify-content-end">
              <Button
                variant="secondary"
                className="me-2"
                onClick={() => setEditModal(null)}
              >
                {t(language, "cancel")}
              </Button>
              <Button variant="primary" type="submit">
                {t(language, "save")}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Modal de Comparação (Admin) */}
      <Modal
        show={compareModal.show}
        onHide={() => setCompareModal({ show: false, calendarData: null, loading: false })}
        size="xl"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.1rem' }}>
            {t(language, "compareTitle") || "Calendar vs Report"} — {labelWeek}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {compareModal.loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : !compareModal.calendarData ? (
            <p className="text-muted text-center py-3">{t(language, "noCalendarData")}</p>
          ) : (
            days.map(day => {
              const calDay = compareModal.calendarData[day];
              const repEntries = weeklyReport[day] || [];
              const calJobs = calDay?.jobs || [];
              const tSize = Math.max(1, calDay?.teamSize || 1);

              // Filtrar "aviso" (case insensitive) de ambos os lados
              const isAviso = name => /aviso/i.test(name);
              const calJobsClean = calJobs.filter(j => !isAviso(j.clientName));
              const repEntriesClean = repEntries.filter(e => !isAviso(e.client));

              if (calJobsClean.length === 0 && repEntriesClean.length === 0) return null;

              // Detectar time correto: qual team tem mais overlap com o report
              const reportClients = new Set(repEntriesClean.map(e => e.client));
              let bestTeam = (calDay?.teams || [])[0] || null;
              let bestCount = 0;
              for (const tk of ['team1', 'team2', 'team3']) {
                const count = calJobsClean.filter(j => j.team === tk && reportClients.has(j.clientName)).length;
                if (count > bestCount) { bestCount = count; bestTeam = tk; }
              }
              const filteredJobs = bestTeam ? calJobsClean.filter(j => j.team === bestTeam) : calJobsClean;

              // Lista unificada por clientName
              const allClients = new Set([
                ...filteredJobs.map(j => j.clientName),
                ...repEntriesClean.map(e => e.client)
              ]);

              return (
                <div key={day} className="mb-4">
                  <h6 className="fw-bold border-bottom pb-1 mb-2">
                    {day}
                    <small className="text-muted ms-2 fw-normal">
                      ({filteredJobs.length} jobs | {tSize} {t(language, "people") || "people"})
                      {bestTeam && <span className="badge bg-light text-dark ms-2">{bestTeam.replace('team', 'T')}</span>}
                    </small>
                  </h6>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.85rem' }}>
                      <thead className="table-light">
                        <tr>
                          <th>{t(language, "client") || "Client"}</th>
                          <th className="text-center" style={{ width: 110 }}>{t(language, "calendarPP") || "Calendar /person"}</th>
                          <th className="text-center" style={{ width: 100 }}>{t(language, "reportLabel") || "Report"}</th>
                          <th className="text-center" style={{ width: 100 }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...allClients].map(clientName => {
                          const calJob = filteredJobs.find(j => j.clientName === clientName);
                          const repEntry = repEntriesClean.find(e => e.client === clientName);

                          // Calcular horas: calendário dividido por pessoas
                          const calTotalMin = calJob ? timeToMinutes(calJob.serviceDuration || "0:00") : 0;
                          const calPerPerson = calJob ? Math.round(calTotalMin / tSize) : 0;
                          const calPPStr = calJob ? minutesToTime(calPerPerson) : null;

                          // Fechamento: editedHours já é por pessoa; originalHours de cadastro precisa dividir
                          const repWorkers = Math.max(1, numWorkers[day] || 1);
                          let repMin = 0;
                          let repPPStr = null;
                          if (repEntry) {
                            if (repEntry.editedHours) {
                              repMin = timeToMinutes(repEntry.editedHours);
                            } else if (repEntry.__manual) {
                              repMin = timeToMinutes(repEntry.originalHours);
                            } else {
                              repMin = Math.round(timeToMinutes(repEntry.originalHours) / repWorkers);
                            }
                            repPPStr = minutesToTime(repMin);
                          }

                          let status, statusClass;
                          if (calJob && repEntry) {
                            const diff = repMin - calPerPerson;
                            if (diff === 0) {
                              status = "OK";
                              statusClass = "text-success";
                            } else {
                              const sign = diff > 0 ? "+" : "";
                              status = `${sign}${diff}min`;
                              statusClass = diff > 0 ? "text-danger" : "text-info";
                            }
                          } else if (calJob && !repEntry) {
                            status = t(language, "missingInReport") || "Missing";
                            statusClass = "text-danger";
                          } else {
                            status = t(language, "extraInReport") || "Extra";
                            statusClass = "text-warning";
                          }

                          return (
                            <tr key={clientName}>
                              <td>{clientName}</td>
                              <td className="text-center">{calPPStr || "—"}</td>
                              <td className="text-center">{repPPStr || "—"}</td>
                              <td className={`text-center fw-bold ${statusClass}`}>{status}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setCompareModal({ show: false, calendarData: null, loading: false })}>
            {t(language, "close") || "Close"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
